// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button } from '@imprivata-cloud/components';
import { Spin } from 'antd';
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './Applications.less';
import { Entitlement } from '@api/constants';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { withRole } from '@common/authorization/Roles';
import { ErrorCode } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { ROLE_TYPE_ADMIN } from '@portal/constants';
import { featureSupported } from '../../../featureFlagsUtils';
import AppProfiles from './appProfiles/AppProfiles';
import { APP_PROFILES_FEATURE_FLAG } from './appProfiles/AppProfiles.types';
import ApplicationsRow from './ApplicationsRow';
import ApplicationsEmpty from './empty/ApplicationsEmpty';
import { useAppConfigQuery } from './hooks/useAppConfig';
import { useAppIntegrationQuery } from './hooks/useAppIntegration';
import HyperdriveModal, { isAppConfigured } from './modals/HyperdriveModal';

const APP_ID = 'epic-hyperdrive';
const TRANSLATION_ROOT = 'apps';

function Applications() {
  const { t } = useTranslation();
  const { tenantInfo } = useSession();
  const { emitError } = useErrorContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [headerEl, setHeaderEl] = useState<Element | null>(null);
  useLayoutEffect(() => {
    setHeaderEl(document.querySelector('.settings-header'));
    return () => setHeaderEl(null);
  }, []);

  const entitled = hasEntitlement(tenantInfo, Entitlement.SSO);

  const {
    data: integrationData,
    isLoading: isIntegrationLoading,
    error: integrationError,
  } = useAppIntegrationQuery(APP_ID, { enabled: entitled });

  const { data: configData, error: configError } = useAppConfigQuery(APP_ID, { enabled: entitled });

  useEffect(() => {
    if (integrationError) {
      emitError(new AppError(integrationError));
    }
  }, [integrationError, emitError]);

  useEffect(() => {
    // `app-not-configured` means the tenant simply hasn't set up Epic
    // Hyperdrive yet — an expected outcome, not a failure to surface.
    if (configError && configError.code !== ErrorCode.APP_NOT_CONFIGURED) {
      emitError(new AppError(configError));
    }
  }, [configError, emitError]);

  if (!entitled) {
    return null;
  }

  const appIntegration = integrationData?.appIntegration;
  const appConfig = configData?.appConfig;
  const isConfigured = !!appIntegration && isAppConfigured(appIntegration, appConfig);

  const renderContent = () => {
    if (isIntegrationLoading) {
      return (
        <div className="applications-loading">
          <Spin />
          <span>{t(`${TRANSLATION_ROOT}.loading`)}</span>
        </div>
      );
    }
    if (!isConfigured) {
      return <ApplicationsEmpty onAdd={() => setIsModalOpen(true)} />;
    }
    return (
      <ApplicationsRow
        appIntegration={appIntegration}
        appConfig={appConfig}
        onEdit={() => setIsModalOpen(true)}
      />
    );
  };

  return (
    <>
      {headerEl && !isConfigured && !isIntegrationLoading &&
        createPortal(
          <Button onClick={() => setIsModalOpen(true)}>
            {t(`${TRANSLATION_ROOT}.add-cta`)}
          </Button>,
          headerEl,
        )}
      <div className="applications-container">
        {renderContent()}
        {isModalOpen && appIntegration && (
          <HyperdriveModal appIntegration={appIntegration} onClose={() => setIsModalOpen(false)} />
        )}
      </div>
      {featureSupported(APP_PROFILES_FEATURE_FLAG) && <AppProfiles />}
    </>
  );
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Applications);
